/**
 * Reference Date Composable
 * Provides the "current date" for date range calculations.
 * Can be overridden by the parent app when the dashboard is embedded (e.g. for timezone or business date).
 *
 * Parent can send via postMessage:
 *   { type: 'DASHBOARD_REFERENCE_DATE', date: '2025-02-19' }  // ISO date string
 *   { type: 'DASHBOARD_REFERENCE_DATE', timestamp: 1739923200 }  // Unix seconds
 *
 * Or via URL param: ?reference_date=2025-02-19
 */

import { ref, computed } from 'vue'

function getReferenceDateFromURL() {
  try {
    let params
    if (window.parent && window.parent !== window) {
      try {
        const parentUrl = new URL(window.parent.location.href)
        params = new URLSearchParams(parentUrl.search)
      } catch {
        params = new URLSearchParams(window.location.search)
      }
    } else {
      params = new URLSearchParams(window.location.search)
    }

    const dateStr = params.get('reference_date')
    if (!dateStr) return null

    const parsed = new Date(dateStr)
    if (!isNaN(parsed.getTime())) return parsed
    return null
  } catch {
    return null
  }
}

let globalMessageListener = null
let overrideRef = null

export function useReferenceDate() {
  if (!overrideRef) {
    const fromUrl = getReferenceDateFromURL()
    overrideRef = ref(fromUrl)

    globalMessageListener = (event) => {
      if (event.data?.type === 'DASHBOARD_REFERENCE_DATE') {
        const { date, timestamp } = event.data
        if (date) {
          const parsed = new Date(date)
          if (!isNaN(parsed.getTime())) {
            overrideRef.value = parsed
          }
        } else if (typeof timestamp === 'number') {
          overrideRef.value = new Date(timestamp * 1000)
        }
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('message', globalMessageListener)
    }
  }

  // When no override: use current time. When override set: use it (fixes wrong dates from parent context)
  return computed(() => overrideRef.value ?? new Date())
}
